import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/api/auth.api';
import { qk } from '@/config/queryKeys';
import { storage } from '@/lib/storage';
import { humanizeError } from '@/utils/errorMessage';
import type { BranchContext, BranchInfo } from '@/types/auth';
import { useSessionStore } from './SessionStore';

/**
 * Current Company + Branch context — the single source of truth for all branch-dependent ERP and
 * AI functionality. Loads the authenticated user's accessible/default/current branches from
 * GET /auth/branch-context and owns branch switching (POST /auth/switch-branch, which persists the
 * current branch SERVER-SIDE — the frontend never sends a branch id header or query/body param).
 *
 * Because the backend scopes every branch-dependent request by the persisted current branch, no
 * module needs to thread a branchId through its requests or query keys. Cache correctness on switch
 * is handled centrally here (see the switch mutation): all branch-dependent caches are REMOVED so
 * no previous-branch data can ever be shown.
 *
 * The selected branch survives refresh because the backend persists it (User.CurrentBranchId); the
 * localStorage id is only a hint, always re-validated against the branches the backend says the user
 * may access. Frontend branch selection is NOT a security boundary — the backend validates
 * access/active on every request and on switch.
 */
interface BranchStoreValue {
  currentBranch: BranchInfo | null;
  currentBranchId: number | null;
  currentBranchName: string | null;
  currentCompany: string | null;
  currentCompanyId: number | null;
  defaultBranch: BranchInfo | null;
  availableBranches: BranchInfo[];
  /** True once loaded and the user has no active branch to work in. */
  hasNoActiveBranch: boolean;
  isLoading: boolean;
  /** True once the branch context has been loaded for the authenticated user (branch-dependent
   *  screens may render). Distinct from isLoading and from error. */
  initialized: boolean;
  isSwitching: boolean;
  error: string | null;
  /** Switches the current branch after validating access; persists + refetches branch data. */
  switchBranch: (branchId: number) => Promise<void>;
}

const BranchContextStore = createContext<BranchStoreValue | undefined>(undefined);

/** Cache roots that are NOT branch-dependent and must survive a branch switch:
 *  - 'auth'            → profile + branch-context (branch-context is re-set on switch below)
 *  - 'public-branding' → tenant-level company branding (identical across branches; keeps the logo
 *                        stable and avoids a needless flicker on switch). */
const BRANCH_INDEPENDENT_ROOTS = new Set<unknown>(['auth', 'public-branding']);

/** Default-branch selection priority (never an inaccessible/inactive branch):
 *  1) backend current branch, 2) backend default branch, 3) previously-selected valid branch,
 *  4) first accessible active branch. All candidates are validated against accessibleBranches. */
function resolveCurrent(ctx: BranchContext | undefined): BranchInfo | null {
  if (!ctx) return null;
  const accessible = ctx.accessibleBranches ?? [];
  if (accessible.length === 0) return null;
  const byId = (id: number | null | undefined) =>
    id == null ? undefined : accessible.find((b) => b.id === id);

  return (
    byId(ctx.currentBranch?.id) ??
    byId(ctx.defaultBranch?.id) ??
    byId(storage.branchId) ??
    accessible[0]
  );
}

export function BranchProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useSessionStore();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: qk.auth.branchContext,
    queryFn: authApi.branchContext,
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });

  const ctx = query.data;
  const availableBranches = useMemo(() => ctx?.accessibleBranches ?? [], [ctx]);
  const currentBranch = useMemo(() => resolveCurrent(ctx), [ctx]);

  // Persist the resolved branch id (hint only) so a later session can prefer it — after validation.
  useEffect(() => {
    if (currentBranch) storage.setBranchId(currentBranch.id);
  }, [currentBranch]);

  const switchMutation = useMutation({
    mutationFn: (branchId: number) => authApi.switchBranch(branchId),
    onSuccess: (updated) => {
      // The server has persisted the new current branch. Adopt its returned context as the new
      // source of truth, and remember the id (validated hint).
      qc.setQueryData(qk.auth.branchContext, updated);
      storage.setBranchId(updated.currentBranch?.id ?? null);
      // Then REMOVE every branch-dependent cache so no previous-branch data can ever be shown:
      // mounted screens drop to a clean loading state and refetch under the new server-side branch
      // scope; unmounted screens start fresh on next visit. Branch-independent caches (auth +
      // tenant branding) are preserved. This is the single, centralized cross-branch isolation
      // point — no module threads a branchId through its own queries.
      qc.removeQueries({ predicate: (q) => !BRANCH_INDEPENDENT_ROOTS.has(q.queryKey[0]) });
    },
  });

  const switchBranch = useCallback(
    async (branchId: number) => {
      if (branchId === currentBranch?.id) return; // no-op: already selected
      // Client-side guard: never send a branch the user does not have access to. The backend
      // re-validates regardless — this only avoids a pointless failing round-trip.
      if (!availableBranches.some((b) => b.id === branchId)) return;
      await switchMutation.mutateAsync(branchId);
    },
    [availableBranches, currentBranch, switchMutation],
  );

  const value = useMemo<BranchStoreValue>(() => {
    const loaded = isAuthenticated && !query.isLoading;
    return {
      currentBranch,
      currentBranchId: currentBranch?.id ?? null,
      currentBranchName: currentBranch?.name ?? null,
      currentCompany: ctx?.tenantName ?? null,
      currentCompanyId: ctx?.tenantId ?? null,
      defaultBranch: ctx?.defaultBranch ?? null,
      availableBranches,
      hasNoActiveBranch: loaded && !query.isError && availableBranches.length === 0,
      isLoading: isAuthenticated && query.isLoading,
      initialized: isAuthenticated && query.isSuccess,
      isSwitching: switchMutation.isPending,
      error: query.isError ? humanizeError(query.error, 'Unable to load your branches.') : null,
      switchBranch,
    };
  }, [
    isAuthenticated,
    ctx,
    currentBranch,
    availableBranches,
    query.isLoading,
    query.isSuccess,
    query.isError,
    query.error,
    switchMutation.isPending,
    switchBranch,
  ]);

  return <BranchContextStore.Provider value={value}>{children}</BranchContextStore.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBranchContext() {
  const ctx = useContext(BranchContextStore);
  if (!ctx) throw new Error('useBranchContext must be used within a BranchProvider');
  return ctx;
}
